import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Map, Filter, Grid3x3, List, Table2, Eye, ArrowLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { NavBar } from '@/components/NavBar';
import { Footer } from '@/components/Footer';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { StickyMobileCTA } from '@/components/StickyMobileCTA';
import { LeadForm } from '@/components/LeadForm';
import { MeetingModal } from '@/components/MeetingModal';
import { ResidencesFilterBar, type FilterOption } from '@/components/ResidencesFilterBar';
import { ResidencesAdvancedFilters } from '@/components/ResidencesAdvancedFilters';
import { ResidencesSearchBar } from '@/components/ResidencesSearchBar';
import { ConfigurationSummaryView } from '@/components/ConfigurationSummaryView';
import { FeaturedConfigurationsView } from '@/components/FeaturedConfigurationsView';
import { ExploreMoreSection } from '@/components/ExploreMoreSection';
import { UnitCard } from '@/components/UnitCard';
import { UnitConfigurationCard } from '@/components/UnitConfigurationCard';
import { UnitLocationMap } from '@/components/UnitLocationMap';
import { UnitDetailModal } from '@/components/UnitDetailModal';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useUnits } from '@/hooks/useUnits';
import { useUnitFilters, type BrowseMode } from '@/hooks/useUnitFilters';
import { useResidencesSearch } from '@/hooks/useResidencesSearch';
import { getUnitsCount, mapConfigFloorToUnitFloor, type PropertyUnit, type UnitConfiguration, type ExtendedUnitFilters } from '@/lib/units';
import { formatFloorDisplay } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import type { FilterCounts } from '@/hooks/useUnitFilters';
import { validateFilterCombination, getActiveFilterLabels } from '@/lib/filter-validation';
import { EmptyFilterState } from '@/components/EmptyFilterState';
import { useToast } from '@/hooks/use-toast';

type ViewMode = 'featured' | 'summary' | 'grid' | 'list';

export const Residences = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize browseMode from URL parameter or default to 'featured'
  const urlView = searchParams.get('view') as BrowseMode | null;
  const [browseMode, setBrowseMode] = useState<BrowseMode>(urlView || 'featured');
  const [viewMode, setViewMode] = useState<ViewMode>('featured');
  const [unitDetailOpen, setUnitDetailOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<PropertyUnit | null>(null);
  const [selectedConfiguration, setSelectedConfiguration] = useState<UnitConfiguration | null>(null);
  const [isDrilledDown, setIsDrilledDown] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [filterCounts, setFilterCounts] = useState<FilterCounts>({
    all: 0,
    '1bed': 0,
    '2bed': 0,
    townhouse: 0,
    lagoon: 0,
    canal: 0,
    available: 0,
  });
  const validationRunRef = useRef<string>('');
  const mainContentRef = useRef<HTMLDivElement>(null);

  const { 
    filters, 
    quickFilter, 
    updateFilters, 
    resetFilters, 
    applyQuickFilter,
    getAccurateCounts,
  } = useUnitFilters(browseMode);
  // When drilled down, load all available units and let client-side filtering handle it
  // Memoize effectiveFilters to prevent unnecessary re-renders
  const effectiveFilters = useMemo(() => {
    return isDrilledDown ? { availability: 'available' as const } : filters;
  }, [isDrilledDown, filters]);
  const { units, configurations, loading, error } = useUnits({ filters: effectiveFilters, autoLoad: true });
  
  // Search functionality
  const { searchQuery, updateSearch, clearSearch, results: searchResults, isSearching } = useResidencesSearch(
    configurations,
    units
  );
  
  // Use search results if searching, otherwise use all data
  const displayConfigurations = isSearching ? searchResults.configurations : configurations;
  let displayUnits = isSearching ? searchResults.units : units;
  
  // When drilled down to a configuration, filter units to match ALL configs with that name
  // This replicates the grouping logic in FeaturedConfigurationsView
  if (isDrilledDown && selectedConfiguration) {
    const matchingConfigs = configurations.filter(c => c.name === selectedConfiguration.name);
    displayUnits = displayUnits.filter((unit) =>
      matchingConfigs.some(
        (config) =>
          unit.block === config.block &&
          unit.unit_type === config.unit_type &&
          unit.bedrooms === config.bedrooms &&
          unit.bathrooms === config.bathrooms &&
          unit.study_room === (config.study_room === 1) &&
          unit.powder_rooms === config.powder_room &&
          (config.location === 'Canal View'
            ? unit.view_type === 'canal'
            : unit.view_type === 'lagoon')
      )
    );
  }

  // WhatsApp number
  const whatsappNumber = '13451234567';
  const whatsappMessage = encodeURIComponent(
    selectedUnit 
      ? `Hi! I'm interested in unit ${selectedUnit.unit_number} at The Pearl at Grand Harbour.`
      : "Hi! I'm interested in learning more about The Pearl at Grand Harbour properties."
  );

  // Load filter counts dynamically based on current browse mode and filters
  useEffect(() => {
    const loadCounts = async () => {
      const counts = await getAccurateCounts(browseMode);
      setFilterCounts(counts);
    };

    loadCounts();
  }, [browseMode, filters, getAccurateCounts]);

  // Sync URL changes to browseMode (handles browser back/forward)
  useEffect(() => {
    const urlView = searchParams.get('view') as BrowseMode | null;
    if (urlView && urlView !== browseMode) {
      setBrowseMode(urlView);
      
      // Update view mode based on browse mode
      if (urlView === 'featured') {
        setViewMode('featured');
      } else if (urlView === 'configurations') {
        setViewMode('summary');
      } else if (urlView === 'all-units') {
        setViewMode('grid');
      }
    } else if (!urlView && browseMode !== 'featured') {
      setBrowseMode('featured');
      setViewMode('featured');
    }
  }, [searchParams, browseMode]);

  // Apply category filter from URL parameter
  useEffect(() => {
    const categoryParam = searchParams.get('category') as '1bed' | '2bed' | 'townhouse' | null;
    if (categoryParam && (categoryParam === '1bed' || categoryParam === '2bed' || categoryParam === 'townhouse')) {
      // Only apply if filters don't already have this category (to avoid unnecessary updates)
      if (filters.category !== categoryParam) {
        applyQuickFilter(categoryParam);
      }
    }
  }, [searchParams, applyQuickFilter, filters.category]);

  useEffect(() => {
    trackEvent('page_view', { page: 'residences' });
    document.title = 'Residences - The Pearl at Grand Harbour';
  }, []);

  // Validate counts on load (development only)
  // Skip validation when filters are active to avoid false errors
  useEffect(() => {
    if (import.meta.env.DEV && configurations.length > 0 && units.length > 0) {
      // Check if any filters are active
      const hasActiveFilters = Object.keys(filters).length > 0 && (
        filters.category !== undefined ||
        filters.viewType !== undefined ||
        filters.floor !== undefined ||
        filters.block !== undefined ||
        filters.bedrooms !== undefined ||
        filters.bathrooms !== undefined ||
        filters.availability !== undefined ||
        filters.minPrice !== undefined ||
        filters.maxPrice !== undefined ||
        filters.minArea !== undefined ||
        filters.maxArea !== undefined
      );
      
      // Skip validation when filters are active
      if (hasActiveFilters) {
        return;
      }
      
      // Create a signature based on data content to prevent re-running on reference changes
      const dataSignature = `${configurations.length}-${units.length}-${configurations.map(c => c.id).join(',')}-${units.map(u => u.id).join(',')}`;
      
      // Only validate if the data signature has changed
      if (validationRunRef.current === dataSignature) {
        return;
      }
      
      validationRunRef.current = dataSignature;
      
      const validateCounts = () => {
        console.group('[Count Validation]');
        let hasErrors = false;
        
        for (const config of configurations) {
          // Map configuration floor description to unit floor enum
          const configFloor = mapConfigFloorToUnitFloor(config.floor);
          const isTownhouse = config.floor === '4-Storey Townhouse';
          const isPenthouse = config.floor.includes('2-Storey Penthouse') || config.floor.includes('2-Storey Apartment');
          
          // Check if configuration matches expected total
          const matchingUnits = units.filter((unit) => {
            // Basic matching criteria
            if (
              unit.block !== config.block ||
              unit.unit_type !== config.unit_type ||
              unit.bedrooms !== config.bedrooms ||
              unit.bathrooms !== config.bathrooms ||
              (config.location === 'Canal View' ? unit.view_type !== 'canal' : unit.view_type !== 'lagoon')
            ) {
              return false;
            }
            
            // Floor matching with special cases
            if (isTownhouse) {
              // Townhouses: match units where floor = 'GF' AND floors_included includes all 4 floors
              return (
                unit.floor === 'GF' &&
                unit.floors_included &&
                unit.floors_included.includes('GF') &&
                unit.floors_included.includes('second') &&
                unit.floors_included.includes('third') &&
                unit.floors_included.includes('fourth')
              );
            } else if (isPenthouse) {
              // Penthouses: match units where floor = 'third' AND 'fourth' in floors_included
              // OR floor = 'fourth' (for standalone penthouses)
              return (
                (unit.floor === 'third' &&
                 unit.floors_included &&
                 unit.floors_included.includes('fourth')) ||
                unit.floor === 'fourth'
              );
            } else if (configFloor) {
              // Standard floors: match by exact floor
              return unit.floor === configFloor;
            }
            
            // If floor mapping failed, skip floor matching (shouldn't happen)
            return true;
          });
          
          if (matchingUnits.length !== config.total_units) {
            console.warn(
              `❌ ${config.name} (${config.unit_type}, Block ${config.block}): Expected ${config.total_units}, Found ${matchingUnits.length} units`
            );
            hasErrors = true;
          }
        }
        
        if (!hasErrors) {
          console.log('✅ All configuration counts match expected values');
        }
        console.groupEnd();
      };
      
      validateCounts();
    }
  }, [configurations, units, filters]);

  // Group units by configuration
  const unitsByConfiguration = useMemo(() => {
    if (!configurations.length || !units.length) return {};

    const grouped: Record<string, PropertyUnit[]> = {};
    
    configurations.forEach((config) => {
      const matchingUnits = units.filter((unit) => {
        // Check if it's a townhouse (3 bedrooms)
        const isTownhouse = config.bedrooms === 3;
        
        // Basic matching criteria
        const basicMatch = 
          unit.block === config.block &&
          unit.unit_type === config.unit_type &&
          unit.bedrooms === config.bedrooms &&
          unit.bathrooms === config.bathrooms &&
          (config.location === 'Canal View' ? unit.view_type === 'canal' : unit.view_type === 'lagoon');
        
        // For townhouses, don't require exact study_room or powder_rooms match
        // (configuration data may be outdated or vary)
        if (isTownhouse) {
          return basicMatch;
        }
        
        // For non-townhouses, require exact match on study_room and powder_rooms
        return basicMatch &&
          unit.study_room === (config.study_room === 1) &&
          unit.powder_rooms === config.powder_room;
      });
      
      if (matchingUnits.length > 0) {
        grouped[config.id] = matchingUnits;
      }
    });

    return grouped;
  }, [configurations, units]);

  const filterOptions: FilterOption[] = [
    { label: 'All', value: 'all', count: filterCounts.all },
    { label: '1 Bedroom', value: '1bed', count: filterCounts['1bed'] },
    { label: '2 Bedroom', value: '2bed', count: filterCounts['2bed'] },
    { label: 'Townhouse', value: 'townhouse', count: filterCounts.townhouse },
    { label: 'Lagoon View', value: 'lagoon', count: filterCounts.lagoon },
    { label: 'Canal View', value: 'canal', count: filterCounts.canal },
    { label: 'Available', value: 'available', count: filterCounts.available },
  ];

  const handleFilterChange = (value: string) => {
    applyQuickFilter(value);
    trackEvent('residences_filter_applied', { filter: value, browseMode });

    // Validate filter combination after applying
    setTimeout(() => {
      const validation = validateFilterCombination(filters);
      if (validation && displayUnits.length === 0) {
        toast({
          title: validation.message,
          description: validation.reason,
          action: (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                updateFilters(validation.suggestedFilters);
                toast({
                  title: 'Filters Updated',
                  description: 'Try this combination instead.',
                });
              }}
            >
              Try Suggestion
            </Button>
          ),
          duration: 6000,
        });
      }
    }, 100);
  };

  const handleUnitQuickView = (unit: PropertyUnit) => {
    setSelectedUnit(unit);
    setUnitDetailOpen(true);
    trackEvent('residences_unit_quick_view', { unit_number: unit.unit_number });
  };

  const handleUnitViewDetails = (unit: PropertyUnit) => {
    navigate(`/residences/${unit.unit_number}`);
    trackEvent('residences_unit_detail_view', { unit_number: unit.unit_number });
  };

  const handleConfigurationClick = async (configuration: UnitConfiguration) => {
    setSelectedConfiguration(configuration);
    setIsDrilledDown(true);
    setViewMode('grid');
    
    // Use handleBrowseModeChange to update URL and trigger scroll-to-top
    handleBrowseModeChange('all-units');
    
    // No database-level filtering - client-side filtering (lines 70-86) handles it
    // This ensures counts match between Featured view and All Units view
    
    trackEvent('residences_configuration_click', { 
      configuration_name: configuration.name,
      configuration_id: configuration.id,
    });
  };

  const handleBackToSummary = () => {
    setIsDrilledDown(false);
    setSelectedConfiguration(null);
    setBrowseMode('featured');
    setViewMode('featured');
    resetFilters();
    trackEvent('residences_back_to_summary', {});
  };

  const handleBrowseModeChange = (mode: BrowseMode) => {
    const previousMode = browseMode;
    setBrowseMode(mode);
    
    // Update URL - clear params for default view, otherwise set view param
    if (mode === 'featured') {
      setSearchParams({});
    } else {
      setSearchParams({ view: mode });
    }
    
    trackEvent('residences_browse_mode_change', { mode, from: previousMode });
    
    // Tab switching intelligence: auto-clear incompatible filters
    if (mode === 'featured' && previousMode !== 'featured') {
      // Featured tab only supports category filters
      const hasNonCategoryFilters = 
        filters.viewType !== undefined ||
        filters.floor !== undefined ||
        filters.block !== undefined ||
        filters.bedrooms !== undefined ||
        filters.bathrooms !== undefined ||
        filters.minPrice !== undefined ||
        filters.maxPrice !== undefined ||
        filters.minArea !== undefined ||
        filters.maxArea !== undefined;

      if (hasNonCategoryFilters) {
        // Keep only category filter, clear others
        const categoryOnly: ExtendedUnitFilters = filters.category 
          ? { category: filters.category, availability: 'available' }
          : { availability: 'available' };
        
        updateFilters(categoryOnly);
        
        toast({
          title: 'Filters Adjusted',
          description: 'Some filters were cleared as they are not available in the Featured view.',
          duration: 4000,
        });
      }
      
      setViewMode('featured');
      setIsDrilledDown(false);
      setSelectedConfiguration(null);
    } else if (mode === 'configurations') {
      setViewMode('summary');
      setIsDrilledDown(false);
      setSelectedConfiguration(null);
    } else if (mode === 'all-units') {
      setViewMode('grid');
      setIsDrilledDown(false);
      setSelectedConfiguration(null);
    }
  };

  // Reset drilled down state when filters are manually changed
  useEffect(() => {
    if (quickFilter === 'all' && !filters.block && !filters.bedrooms && !filters.bathrooms && !filters.viewType) {
      setIsDrilledDown(false);
      setSelectedConfiguration(null);
    }
  }, [quickFilter, filters]);

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
    trackEvent('whatsapp_click', { location: 'residences_page', unit: selectedUnit?.unit_number });
  };

  const handleSitePlanNavigate = () => {
    trackEvent('residences_site_plan_navigate', { destination: '/site-plan' });
    navigate('/site-plan');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          mainContentRef.current?.focus();
          mainContentRef.current?.scrollIntoView({ behavior: 'smooth' });
        }}
      >
        Skip to main content
      </a>

      {/* Navigation */}
      <NavBar 
        onRegisterClick={() => setLeadFormOpen(true)} 
        onConsultClick={() => setMeetingOpen(true)}
      />

      {/* Hero Section */}
      <section className="relative h-[50vh] lg:h-[60vh] overflow-hidden pt-20" role="banner">
        <div className="absolute inset-0 bg-gradient-to-br from-deep-navy via-midnight-blue to-deep-navy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="font-display text-4xl lg:text-6xl font-bold mb-6">
              Your Pearl Awaits: A Limited Collection of 74 Residences
            </h1>
            <div className="text-xl lg:text-2xl text-white/90 mb-8 max-w-3xl mx-auto space-y-3">
              <p>
                The next chapter of Grand Harbour is taking shape—an opportunity to live by the water in a way that feels effortless and deeply connected. Completing in 2028, these residences place everyday life against a backdrop of calm horizons and open sky.
              </p>
              <p className="text-lg lg:text-xl text-white/85">
                The Canal Residence offers quiet seclusion along wide, tranquil waterways.
              </p>
              <p className="text-lg lg:text-xl text-white/85">
                The Lagoon Residence brings you to the edge of a 20,000 SF lagoon, where soft shorelines and a laid-back, beach-inspired rhythm define your days.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-lg">
              <span>{units.length} Units Available</span>
              <span>•</span>
              <span>2028 Completion</span>
              <span>•</span>
              <span>Lagoon-Side Living</span>
            </div>
          </div>
        </div>
      </section>

      {/* Site Plan CTA Section */}
      <section className="py-12 bg-soft-cream">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl font-bold mb-4">Explore Our Site Plan</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Click on any unit in our interactive site plan to view details, floor plans, and availability.
            </p>
            <Button
              size="lg"
              className="bg-brand-teal hover:bg-brand-teal/90 text-white"
              onClick={handleSitePlanNavigate}
            >
              <Map className="w-5 h-5 mr-2" />
              Explore Interactive Site Plan
            </Button>
          </div>
        </div>
      </section>

      {/* Browse Mode Tabs */}
      <section className="py-6 bg-background border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={browseMode} onValueChange={(value) => handleBrowseModeChange(value as BrowseMode)}>
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3">
              <TabsTrigger value="featured" className="gap-2">
                <Star className="w-4 h-4" />
                Featured
              </TabsTrigger>
              <TabsTrigger value="configurations" className="gap-2">
                <Table2 className="w-4 h-4" />
                Configurations
              </TabsTrigger>
              <TabsTrigger value="all-units" className="gap-2">
                <Grid3x3 className="w-4 h-4" />
                All Units
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </section>

      {/* Search Bar - Show only in configurations and all-units modes */}
      {(browseMode === 'configurations' || browseMode === 'all-units') && (
        <section className="py-6 bg-background border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <ResidencesSearchBar
              value={searchQuery}
              onChange={updateSearch}
              onClear={clearSearch}
            />
          </div>
        </section>
      )}

      {/* Breadcrumb Navigation - Show when drilled down */}
      {isDrilledDown && selectedConfiguration && (
        <section className="py-4 bg-background border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToSummary}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Summary
              </Button>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      asChild
                      className="cursor-pointer"
                      onClick={handleBackToSummary}
                    >
                      <span>All Configurations</span>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator>
                    <ChevronRight className="w-4 h-4" />
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbPage>{selectedConfiguration.name}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </div>
        </section>
      )}

      {/* Filter Controls - Show in all modes */}
      <section 
        className="py-6 bg-background border-b"
        aria-label="Unit filters"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-4">
            <ResidencesFilterBar
              filters={filterOptions}
              activeFilter={quickFilter}
              onFilterChange={handleFilterChange}
              browseMode={browseMode}
              loading={loading}
            />
            {(browseMode === 'configurations' || browseMode === 'all-units') && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAdvancedFiltersOpen(true)}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Advanced Filters
                </Button>
                {browseMode === 'all-units' && (
                  <div className="flex border rounded-lg">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      className="rounded-r-none"
                    >
                      <Grid3x3 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className="rounded-l-none"
                    >
                      <List className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main 
        id="main-content"
        ref={mainContentRef}
        className={browseMode === 'featured' ? 'py-0' : 'py-12'}
        tabIndex={-1}
        aria-label="Residences main content"
      >
        <div className={browseMode === 'featured' ? '' : 'container mx-auto px-4 sm:px-6 lg:px-8'}>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="h-48 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-destructive mb-4">Error loading units: {error.message}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          ) : browseMode === 'featured' ? (
            displayConfigurations.length === 0 ? (
              <EmptyFilterState
                browseMode={browseMode}
                activeFilters={getActiveFilterLabels(filters)}
                onClearFilters={resetFilters}
                onViewAllUnits={() => handleBrowseModeChange('all-units')}
              />
            ) : (
              <FeaturedConfigurationsView
                configurations={displayConfigurations}
                units={displayUnits}
                onConfigurationClick={handleConfigurationClick}
                onRegisterClick={() => setLeadFormOpen(true)}
                activeFilters={filters}
              />
            )
          ) : displayUnits.length === 0 && displayConfigurations.length === 0 ? (
            <EmptyFilterState
              browseMode={browseMode}
              activeFilters={getActiveFilterLabels(filters)}
              onClearFilters={resetFilters}
              onViewAllUnits={() => browseMode === 'configurations' ? handleBrowseModeChange('all-units') : handleBrowseModeChange('configurations')}
            />
          ) : browseMode === 'configurations' && !isDrilledDown ? (
            displayConfigurations.length === 0 ? (
              <EmptyFilterState
                browseMode={browseMode}
                activeFilters={getActiveFilterLabels(filters)}
                onClearFilters={resetFilters}
                onViewAllUnits={() => handleBrowseModeChange('all-units')}
              />
            ) : (
              <ConfigurationSummaryView
                configurations={displayConfigurations}
                units={displayUnits}
                onConfigurationClick={handleConfigurationClick}
                onUnitClick={handleUnitViewDetails}
              />
            )
          ) : viewMode === 'grid' ? (
            displayUnits.length === 0 ? (
              <EmptyFilterState
                browseMode={browseMode}
                activeFilters={getActiveFilterLabels(filters)}
                onClearFilters={resetFilters}
                onViewAllUnits={() => handleBrowseModeChange('configurations')}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{displayUnits.map((unit) => (
                <UnitCard
                  key={unit.id}
                  unit={unit}
                  onQuickView={handleUnitQuickView}
                  onViewDetails={handleUnitViewDetails}
                />
                ))}
              </div>
            )
          ) : (
            <div className="space-y-4">
              {displayUnits.map((unit) => (
                <div
                  key={unit.id}
                  className="flex flex-col p-6 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleUnitViewDetails(unit)}
                >
                  <div className="flex items-center gap-4 lg:gap-6">
                    <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      {unit.images && unit.images.length > 0 ? (
                        <img
                          src={unit.images[0]}
                          alt={`Unit ${unit.unit_number}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.svg';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          No Image
                        </div>
                      )}
                    </div>
                    {/* Location Map - visible on lg+ screens */}
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted/30 flex-shrink-0 hidden lg:flex items-center justify-center border">
                      <UnitLocationMap 
                        unitNumber={unit.unit_number}
                        compact
                        showTitle={false}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-display text-lg lg:text-xl font-bold">Unit {unit.unit_number}</h3>
                          <p className="text-sm text-muted-foreground">{formatFloorDisplay(unit.floor)} • {unit.view_type === 'lagoon' ? 'Lagoon View' : 'Canal View'}</p>
                        </div>
                        <Badge variant={unit.availability === 'available' ? 'default' : 'secondary'}>
                          {unit.availability}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span>{unit.bedrooms} bed</span>
                        <span>{unit.bathrooms} bath</span>
                        <span>{unit.area_total ? `${unit.area_total.toLocaleString()} sq.ft` : 'N/A'}</span>
                        {unit.show_price && unit.price && (
                          <span className="font-semibold text-brand-teal">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(unit.price)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* View button - visible on lg+ screens inline */}
                    <Button variant="outline" size="sm" className="flex-shrink-0 hidden lg:flex">
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                  </div>
                  {/* View button - visible below lg, at bottom */}
                  <div className="flex justify-end mt-4 lg:hidden">
                    <Button variant="outline" size="sm">
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Explore More Section */}
      <ExploreMoreSection 
        onContactClick={() => setLeadFormOpen(true)}
        currentPage="residences"
      />

      {/* Footer */}
      <Footer />

      {/* Persistent Conversion UI */}
      <StickyMobileCTA onClick={() => setLeadFormOpen(true)} />
      <WhatsAppButton onClick={handleWhatsAppClick} />
      <ScrollToTopButton />

      {/* Modals */}
      <UnitDetailModal
        open={unitDetailOpen}
        onOpenChange={setUnitDetailOpen}
        unit={selectedUnit}
        onRegisterClick={() => {
          setUnitDetailOpen(false);
          setLeadFormOpen(true);
        }}
        onConsultClick={() => {
          setUnitDetailOpen(false);
          setMeetingOpen(true);
        }}
      />
      <LeadForm 
        open={leadFormOpen} 
        onOpenChange={setLeadFormOpen}
        unitNumber={selectedUnit?.unit_number}
      />
      <MeetingModal
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        onWhatsAppClick={handleWhatsAppClick}
        unitNumber={selectedUnit?.unit_number}
      />
      <ResidencesAdvancedFilters
        open={advancedFiltersOpen}
        onOpenChange={setAdvancedFiltersOpen}
        filters={filters}
        onFiltersChange={updateFilters}
        onApply={() => {
          trackEvent('residences_filter_applied', { source: 'advanced' });
        }}
        onReset={resetFilters}
      />
    </div>
  );
};

